import * as React from 'react';
import HistoryRowListing from '../../../../components/HistoryRowListing';
import { IHistoryContext, withFormContext } from '../../../../containers/FormContextHandler';
import { formatRowData, isISeizureFreePeriod } from '../../utils';
import HistoryRowControls from '../../../../components/HistoryRowControls';
import { Container, Item } from '../../../../components/Grid';
import { calculateIntervalYMD, isPartialDate, nowPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { sortDocuments } from '../../../../utility/randomUtil';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { styled } from '@mui/system';

const DateContainer = styled('div')({
  display: 'inline-block',
  whiteSpace: 'nowrap',
  fontWeight: 600,
});

const itemSpace = 4;

/** Make a JSX row from a seizureFreePeriod document. */
const makeRow = (d: ISeizureFreePeriod): React.JSX.Element => {
  const { seizureFreePeriodDetails, startDate, endDate } = d;
  const endingDate = endDate ? endDate : nowPartialDate();
  const diff = startDate && calculateIntervalYMD(startDate, endingDate);
  const data = {
    seizureFreePeriod: [startDate, endDate],
    seizureFreePeriodLength: startDate ? diff : undefined,
    seizureFreePeriodDetails,
  };
  const dataRow: Array<React.ReactNode> = [];
  (Object.keys(data) as Array<keyof typeof data>).forEach(key => {
    const value = data[key];
    dataRow.push(
      <Item key={key} xs={itemSpace}>
        {key === 'seizureFreePeriod' && Array.isArray(value) ? (
          <div style={{ paddingRight: '1rem' }}>
            <DateContainer>
              {formatPartialDate(value[0])}
              {isPartialDate(value[1]) && '\u00A0-\u00A0'}
            </DateContainer>
            {isPartialDate(value[1]) && <DateContainer>{formatPartialDate(value[1])}</DateContainer>}
          </div>
        ) : (
          formatRowData(key, value)
        )}
      </Item>,
    );
  });
  return <Container>{dataRow}</Container>;
};

/** Make a JSX header row for seizureFreePeriod documents. */
const makeHeaders = (texts: string[]): React.JSX.Element => {
  const headerRow: Array<React.ReactNode> = texts.map((t, idx) => (
    <Item key={idx} xs={itemSpace}>
      {t}
    </Item>
  ));
  headerRow.push(<Item key="tail" xs={4} />);
  return <Container>{headerRow}</Container>;
};

const SeizureFreePeriods = ({ documents, fm, startEdit }: IOwnProps): React.JSX.Element => {
  if (!fm || !startEdit) throw new Error('fm or startEdit is undefined');
  const seizFreePerDocs = documents ? (documents.filter(d => isISeizureFreePeriod(d)) as ISeizureFreePeriod[]) : [];
  const headerTexts = ['period', 'seizureFreePeriodLength', 'seizureFreePeriodDetails'].map(t => fm(`seizure.${t}`));

  seizFreePerDocs.sort((a, b) => sortDocuments([{ type: 'date', sortField: 'date' }])(a, b));
  return (
    <>
      <div
        style={{
          display: 'flex',
          flex: 1,
          justifyContent: 'flex-end',
          marginBottom: '2rem',
        }}
      >
        <ActionButtonRounded
          text={'seizure.addNewSeizureFreePeriod'}
          onClick={startEdit && startEdit({}, 'seizureFreePeriod')}
          filled
          uppercase={false}
          width={20}
          height={4}
          fontSize={16}
        />
      </div>
      <HistoryRowListing
        contentFormat={makeRow}
        headers={makeHeaders(headerTexts)}
        documents={seizFreePerDocs}
        dateFieldName="startDate"
        makeControls
        historyRowControls={(d: IControlProps) => <HistoryRowControls document={d} startEdit={startEdit} />}
      />
    </>
  );
};

interface IOwnProps extends IHistoryContext {
  documents?: Array<ISeizureDoc>;
}

export default withFormContext(SeizureFreePeriods);
